import { useEffect, useState } from "react";
import config from "../config";
import pkceChallenge from "../lib/pkce";
import { useParams } from "react-router-dom";
import queryString from "query-string";

export enum AuthOrigin {
  Login = "login",
  Register = "register",
}

export interface AuthState {
  site: string;
  redirect?: string;
  isEmployee: boolean;
  masquerade?: string;
  authOrigin: AuthOrigin;
  state: string;
  isMobileLogin?: boolean;
  appName?: string;
}

interface OAuthConfig {
  authEndpoint: string;
  clientId: string;
  scope: string;
  responseType: string;
  additionalParams?: Record<string, string>;
}

export enum OAuthIDP {
  Microsoft = "Microsoft",
  GoGov = "GoGov",
  Google = "Google",
  Facebook = "Facebook",
  Apple = "Apple",
}

const redirectUrl = config.OAUTH_REDIRECT_URL;
const appleRedirectUrl = config.OAUTH_APPLE_REDIRECT_URL;
const ProviderConfig: Record<OAuthIDP, OAuthConfig> = {
  [OAuthIDP.Microsoft]: {
    authEndpoint: "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
    clientId: config.OAUTH_MICROSOFT_CLIENT_ID ?? "",
    scope: "openid profile email offline_access",
    responseType: "code id_token",
    additionalParams: {
      response_mode: "fragment",
      prompt: "select_account",
    },
  },
  [OAuthIDP.GoGov]: {
    authEndpoint: `https://login.microsoftonline.com/${config.OAUTH_GOGOV_TENANT_ID}/oauth2/v2.0/authorize`,
    clientId: config.OAUTH_GOGOV_CLIENT_ID ?? "",
    scope: "openid profile email offline_access",
    responseType: "code id_token",
    additionalParams: {
      response_mode: "fragment",
      prompt: "select_account",
    },
  },
  [OAuthIDP.Google]: {
    authEndpoint: "https://accounts.google.com/o/oauth2/v2/auth",
    clientId: config.OAUTH_GOOGLE_CLIENT_ID ?? "",
    scope: "openid profile email",
    responseType: "code id_token",
    additionalParams: {
      access_type: "offline",
      prompt: "select_account",
    },
  },
  [OAuthIDP.Facebook]: {
    authEndpoint: "https://facebook.com/dialog/oauth/",
    clientId: config.OAUTH_FACEBOOK_CLIENT_ID ?? "",
    scope: "openid public_profile email",
    responseType: "code id_token",
    additionalParams: {},
  },
  [OAuthIDP.Apple]: {
    authEndpoint: "https://appleid.apple.com/auth/authorize",
    clientId: config.OAUTH_APPLE_CLIENT_ID ?? "",
    scope: "name email",
    responseType: "code id_token",
    additionalParams: {
      response_mode: "form_post",
    },
  },
};

export const useOAuth = ({ isEmployee, authOrigin }: { isEmployee: boolean; authOrigin: AuthOrigin }) => {
  const query = queryString.parse(window.location.search);
  const [error, setError] = useState<string | null>(null);
  const { site } = useParams<{ site: string }>();

  const initiateOAuth = async (idp: OAuthIDP, masquerade?: string, isMobileLogin?: boolean, appName?: string) => {
    try {
      if (masquerade) idp = OAuthIDP.GoGov;
      const { authEndpoint, clientId, scope, responseType, additionalParams } = ProviderConfig[idp];
      const { code_verifier: verifier, code_challenge: challenge } = await pkceChallenge(64);
      const redirect = query.redirect || "";
      const stateJson: AuthState = {
        site,
        redirect: redirect as string,
        isEmployee,
        masquerade,
        authOrigin,
        state: `${Math.random().toString(36).substring(2, 10)}`,
        isMobileLogin: isMobileLogin ?? false,
        appName: appName ?? undefined,
      };
      const state = JSON.stringify(stateJson);
      const nonce = Math.random().toString(36).substring(2, 10);

      const url = new URL(authEndpoint);
      url.search = new URLSearchParams({
        ...additionalParams,
        client_id: clientId,
        response_type: responseType,
        redirect_uri: idp === OAuthIDP.Apple ? appleRedirectUrl : redirectUrl,
        scope: scope,
        state,
        nonce,
        code_challenge: challenge,
        code_challenge_method: "S256",
      }).toString();

      localStorage.setItem("code_verifier", verifier);
      localStorage.setItem("state", state);
      localStorage.setItem("nonce", nonce);
      localStorage.setItem("idp", idp);

      window.location.href = url.toString();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    if (query.authErr) setError(query.authErr as string);
  }, []);

  return {
    error,
    initiateOAuth,
  };
};
